import { Children, ReactNode, RefObject, useCallback, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ScrollEndFade } from '@/components/ScrollEndFade';
import { cn } from '@/lib/utils';

interface EventFeedListProps {
    children: ReactNode;
    scrollContainerRef?: RefObject<HTMLDivElement | null>;
    onScroll?: () => void;
    className?: string;
    'aria-busy'?: boolean;
    footer?: ReactNode;
}

export function EventFeedList({
    children,
    scrollContainerRef,
    onScroll,
    className,
    'aria-busy': ariaBusy,
    footer,
}: EventFeedListProps) {
    const [scrollContainer, setScrollContainer] =
        useState<HTMLDivElement | null>(null);

    const attachScrollContainer = useCallback(
        (element: HTMLDivElement | null) => {
            if (scrollContainerRef) {
                scrollContainerRef.current = element;
            }
            setScrollContainer(element);
        },
        [scrollContainerRef],
    );

    return (
        <div className="relative flex-1 min-h-0">
            <div
                ref={attachScrollContainer}
                onScroll={onScroll}
                className={cn(
                    'absolute inset-0 overflow-y-auto overflow-x-hidden scrollbar-hide',
                    className,
                )}
                aria-busy={ariaBusy}
            >
                <div className="flex flex-col gap-4 overflow-x-clip">
                    <AnimatePresence initial={false}>
                        {children}
                    </AnimatePresence>
                    {footer}
                </div>
            </div>

            <ScrollEndFade
                container={scrollContainer}
                contentVersions={[Children.count(children)]}
                className="bg-[linear-gradient(180deg,rgba(17,18,20,0),var(--grey-700)_83%)]"
            />
        </div>
    );
}
